import { ACTION_TYPES } from "@/utils/ai/utils";
import { create } from "zustand";
import _ from "lodash";
import { Message, MessageContext, useSupabaseStore } from "./supabaseStore";
import { useMemo } from "react";

// Helper functions for localStorage persistence
const AGENT_CHOICE_KEY = "tempo-ai-agent-choice";
const CODE_GENERATION_MODEL_KEY = "tempo-ai-code-generation-model";
const MODEL_MIGRATION_KEY = "tempo-ai-model-migrated-v1";

const loadPersistedModel = (): AI_MODEL => {
  try {
    const hasMigrated = localStorage.getItem(MODEL_MIGRATION_KEY);

    if (!hasMigrated) {
      localStorage.setItem(MODEL_MIGRATION_KEY, "true");
      localStorage.setItem(CODE_GENERATION_MODEL_KEY, AI_MODEL.AUTO);
      return AI_MODEL.AUTO;
    }

    const stored = localStorage.getItem(CODE_GENERATION_MODEL_KEY);
    if (stored && Object.values(AI_MODEL).includes(stored as AI_MODEL)) {
      return stored as AI_MODEL;
    }
  } catch {
    // localStorage not available (SSR, privacy mode, etc.)
  }
  return AI_MODEL.AUTO;
};

const persistModel = (model: AI_MODEL) => {
  try {
    localStorage.setItem(CODE_GENERATION_MODEL_KEY, model);
  } catch {
    // localStorage not available - continue without persistence
  }
};

const loadPersistedAgent = (): AI_AGENT => {
  try {
    const stored = localStorage.getItem(AGENT_CHOICE_KEY);
    if (stored && Object.values(AI_AGENT).includes(stored as AI_AGENT)) {
      return stored as AI_AGENT;
    }
  } catch {
    // localStorage not available (SSR, privacy mode, etc.)
  }
  return AI_AGENT.MAX_AGENT;
};

const persistAgent = (agent: AI_AGENT) => {
  try {
    localStorage.setItem(AGENT_CHOICE_KEY, agent);
  } catch {
    // localStorage not available
  }
};

export enum AI_MODEL {
  GEMINI_2_0_FLASH = "gemini",
  CLAUDE_4_6_OPUS = "claude-4-6-opus",
  CLAUDE_4_6_SONNET = "claude-4-6-sonnet",
  CLAUDE_4_5_HAIKU = "claude-4-5-haiku",
  GEMINI_3_1_PRO = "gemini-3.1-pro",
  GPT_5 = "gpt-5",
  GPT_5_MINI = "gpt-5-mini",
  GPT_5_NANO = "gpt-5-nano",
  GPT_52 = "gpt-5.2",
  GPT_53_CODEX = "gpt-5.3-codex",
  AUTO = "auto",
}

export enum AI_AGENT {
  MAX_AGENT = "max-agent",
  CLAUDE_AGENT = "claude-agent",
  PM_AGENT = "pm-agent",
}

// Note: As of Oct 2025, web_search tool is NOIT support on AzureOpenAI
// Whereas for Gemini, including function tool and provider defined tools
// cannot be used on the same request.
export const SEARCH_SUPPORTED_MODELS = new Set([
  AI_MODEL.CLAUDE_4_6_SONNET,
  AI_MODEL.CLAUDE_4_6_OPUS,
  AI_MODEL.CLAUDE_4_5_HAIKU,
]);

// Define which models each agent supports
export const AGENT_SUPPORTED_MODELS: Record<AI_AGENT, Set<AI_MODEL>> = {
  [AI_AGENT.MAX_AGENT]: new Set([
    AI_MODEL.GEMINI_2_0_FLASH,
    AI_MODEL.CLAUDE_4_6_SONNET,
    AI_MODEL.CLAUDE_4_6_OPUS,
    AI_MODEL.CLAUDE_4_5_HAIKU,
    AI_MODEL.GEMINI_3_1_PRO,
    AI_MODEL.GPT_5,
    AI_MODEL.GPT_52,
    AI_MODEL.GPT_53_CODEX,
    AI_MODEL.AUTO,
  ]),
  [AI_AGENT.CLAUDE_AGENT]: new Set([
    AI_MODEL.CLAUDE_4_6_OPUS,
    AI_MODEL.CLAUDE_4_6_SONNET,
    AI_MODEL.CLAUDE_4_5_HAIKU,
  ]),
  [AI_AGENT.PM_AGENT]: new Set([
    AI_MODEL.CLAUDE_4_6_SONNET,
    AI_MODEL.CLAUDE_4_6_OPUS,
    AI_MODEL.GPT_5,
  ]),
};

// As of Oct 2025, Reasoning with Azure OpenAI does not work.
// The reasoning tokens are counted but they arent returned on the response.
// TODO: Remove GPT_5 from the list when azure AI sdk fixes the bug.
export const REASONING_UNSUPPORTED_MODELS = [AI_MODEL.GPT_5];

export interface AiActionStore {
  actionType: ACTION_TYPES;
  selectedConversationId: string | null;

  setActionType: (actionType: ACTION_TYPES) => void;
  setSelectionConversationId: (selectedConversationId: string | null) => void;

  chatStreamingMessages: Message[] | null;
  setChatStreamingMessages: (messages: Message[] | null) => void;

  setChatMessageContext: (messageId: string, context: MessageContext) => void;

  resetAiActionStore: () => void;

  agent: AI_AGENT;
  setAgent: (agent: AI_AGENT) => void;

  codeGenerationModel: AI_MODEL;
  setCodeGenerationModel: (
    model: AI_MODEL,
    onConversationSwitch?: () => Promise<void>,
  ) => void;

  tokenSoftLimit: number;
  setTokenSoftLimit: (limit: number) => void;

  stopAiTrigger: number;
  setStopAiTrigger: () => void;

  canvasPromptInput: string;
  chatPromptInput: string;
  setPromptInput(type: "canvas" | "chat", prompt: string): void;

  streamingTokenCount: number;
  setStreamingTokenCount: (count: number) => void;
  resetStreamingTokenCount: () => void;
}

export const useAiActionStore = create<AiActionStore>(
  (set, get): AiActionStore => {
    return {
      actionType: ACTION_TYPES.GENERATE,
      selectedConversationId: null,
      codeGenerationModel: loadPersistedModel(),
      agent: loadPersistedAgent(),
      setCodeGenerationModel: (model, onConversationSwitch) => {
        set({ codeGenerationModel: model });
        persistModel(model);

        // Trigger conversation switching if callback provided
        if (onConversationSwitch) {
          onConversationSwitch().catch((error) => {
            console.error(
              "Failed to switch conversation after model change:",
              error,
            );
          });
        }
      },

      setAgent: (agent) => {
        set({ agent });
        persistAgent(agent);
      },

      setActionType: (actionType: ACTION_TYPES) => {
        set({ actionType });
      },
      setSelectionConversationId: (selectionConversationId) => {
        set({ selectedConversationId: selectionConversationId });
      },

      chatStreamingMessages: null,
      setChatStreamingMessages: (messages) => {
        set({ chatStreamingMessages: messages });
      },

      setChatMessageContext: (messageId, context) => {
        const messages = get().chatStreamingMessages;

        if (!messages) {
          return;
        }

        const newMessages = messages?.map((message) => {
          if (message.id === messageId) {
            return { ...message, message_context: context };
          }

          return message;
        });

        set({ chatStreamingMessages: newMessages });
      },

      resetAiActionStore: () => {
        set({
          actionType: ACTION_TYPES.GENERATE,
          selectedConversationId: null,
          chatStreamingMessages: null,
          codeGenerationModel: loadPersistedModel(),
          agent: loadPersistedAgent(),
          canvasPromptInput: "",
          chatPromptInput: "",
          stopAiTrigger: 0,
          tokenSoftLimit: 200,
          streamingTokenCount: 0,
        });
      },

      tokenSoftLimit: 200,
      setTokenSoftLimit: (limit) => {
        set({ tokenSoftLimit: limit });
      },

      stopAiTrigger: 0,
      setStopAiTrigger: () => {
        set({ stopAiTrigger: get().stopAiTrigger + 1 });
      },

      canvasPromptInput: "",
      chatPromptInput: "",
      setPromptInput: (type, prompt) => {
        if (type === "canvas") {
          set({ canvasPromptInput: prompt });
        } else {
          set({ chatPromptInput: prompt });
        }
      },

      streamingTokenCount: 0,
      setStreamingTokenCount: (count) => {
        set({ streamingTokenCount: count });
      },
      resetStreamingTokenCount: () => {
        set({ streamingTokenCount: 0 });
      },
    };
  },
);

export const useCurrentConvo = () => {
  const conversationId = useAiActionStore(
    (state) => state.selectedConversationId,
  );
  const conversations = useSupabaseStore((state) => state.aiChatConversations);
  const messages = useSupabaseStore((state) => state.aiChatMessages);

  return useMemo(() => {
    // @ts-expect-error TS2538
    const conversation = conversations[conversationId];

    if (!conversation) {
      return {};
    }

    const messagesForConvo: Message[] = Object.values(messages)
      .filter((message) => message.conversation_id === conversationId)
      ?.sort((a, b) => {
        return (
          new Date(a?.created_at).getTime() - new Date(b?.created_at).getTime()
        );
      });

    return {
      conversation,
      messages: messagesForConvo,
    };
  }, [conversationId, conversations, messages]);
};
